import type { ReactNode } from 'react';
import { Breadcrumbs, type BreadcrumbItem } from '../../molecules/Breadcrumbs/Breadcrumbs';
import { DatePicker, type DatePickerProps } from '../DatePicker/DatePicker';
import styles from './TopNavigationBar.module.css';

/**
 * Figma: `Top Navigation Bar` (214:6542) — "Persistent top-level navigation bar
 * with breadcrumbs and page-level actions."
 *
 * The set carries three component properties:
 *
 *   Type (VARIANT)          Breadcrumb | Title   default Breadcrumb
 *   Date Picker (BOOLEAN)   default true
 *   Buttons (BOOLEAN)       default false
 *
 * `Type` is the one that changes the composition. `Type=Breadcrumb` is the
 * wayfinding bar — breadcrumbs left, an actions wrapper right. `Type=Title`
 * (843:29444) is a single left-aligned page title and nothing else: its
 * `Wrapper` holds one text layer, and neither boolean toggle is wired to
 * anything inside it. So `comparison` and `actions` are breadcrumb-only, which
 * is why they are ignored rather than rendered when `type` is `title`.
 *
 * The two booleans are the `comparison` and `actions` props: Figma's toggles
 * show and hide the `Date Picker Wrapper` and `Actions  Row` frames, and an
 * absent prop is the same statement. Their defaults are Figma's — a bar arrives
 * with date pickers and without a CTA.
 */
export type TopNavigationBarComparison = {
  /** Figma's first `Date picker dropdown` — "This week" on the canvas. */
  primary: DatePickerProps;
  /**
   * Figma's second — "Previous week". **Optional**, which the component set
   * does not express: the Influencers detail frames (964:12441, 972:25035)
   * show a single picker, and they get there by hiding the separator and the
   * second dropdown by hand rather than by setting a property. There is no
   * variant for it, so the capability is added here rather than read off the
   * file — a screen that does not compare periods should not have to pass a
   * second range it will not draw.
   *
   * Omitted, the separator goes with it: "vs." with nothing after it is worse
   * than no separator at all.
   */
  compare?: DatePickerProps;
  /** Figma's literal text between them. */
  separator?: string;
};

export type TopNavigationBarProps = {
  /** Figma `Type`. */
  type?: 'breadcrumb' | 'title';
  /**
   * Figma `Breadcrumbs` (200:2544). Used by `type="breadcrumb"`; ignored by
   * `type="title"`, whose Figma variant has no breadcrumb trail.
   */
  breadcrumbs?: BreadcrumbItem[];
  /**
   * Figma `Type=Title`'s `Text` layer — "Page title" on the canvas. Ignored by
   * `type="breadcrumb"`.
   */
  title?: string;
  /**
   * Heading level for `title`. Not in Figma, which has no concept of a heading
   * — but this is the page's title in the banner, so it is a heading, and the
   * level has to be the page's call rather than the component's.
   */
  titleLevel?: 1 | 2 | 3;
  /**
   * Figma `Date Picker Wrapper`, toggled by the `Date Picker` boolean. Omit to
   * render the bar without one.
   *
   * **Both types**, since 2026-09-17. It was breadcrumb-only here because every
   * frame in the file until then was breadcrumb; the Influencers list
   * (964:6974) is the first `Type=Title` frame and it carries the identical
   * `Actions Wrapper` — two pickers and a button — so the restriction was an
   * accident of which screens had been built, not a reading of the design.
   */
  comparison?: TopNavigationBarComparison;
  /**
   * Figma `Actions  Row` (Figma's double space), toggled by the `Buttons`
   * boolean. Omit for a bar with no CTA. Both types — see `comparison`.
   */
  actions?: ReactNode;
  /**
   * Accessible name for the breadcrumb landmark, forwarded to `Breadcrumbs`.
   * Not in Figma.
   */
  breadcrumbLabel?: string;
  className?: string;
};

export function TopNavigationBar({
  type = 'breadcrumb',
  breadcrumbs,
  title,
  titleLevel = 1,
  comparison,
  actions,
  breadcrumbLabel,
  className,
}: TopNavigationBarProps) {
  const Heading = `h${titleLevel}` as const;

  /*
   * Figma `Actions Wrapper` — identical in both variants, so it is built once
   * and placed in each rather than duplicated. Rendered as `null` when there is
   * nothing to put in it, which is what lets `Type=Title` keep its MIN-aligned
   * single-child wrapper in the case the file originally drew.
   */
  const actionsWrapper =
    comparison || actions ? (
      <div className={styles.actionsWrapper}>
        {comparison && (
          <div className={styles.comparison}>
            <DatePicker {...comparison.primary} />
            {comparison.compare && (
              <>
                {/*
                 * Decorative: the two pickers are already labelled, and "vs."
                 * read aloud between them adds nothing an adjacent control
                 * does not. Not in Figma, which has only a text layer.
                 */}
                <span className={styles.separator} aria-hidden="true">
                  {comparison.separator ?? 'vs.'}
                </span>
                <DatePicker {...comparison.compare} />
              </>
            )}
          </div>
        )}

        {actions && <div className={styles.actionsRow}>{actions}</div>}
      </div>
    ) : null;

  return (
    /*
     * `<header>` rather than a div. Not in Figma — Figma has no concept of a
     * landmark, but this is the page banner and screen-reader users navigate by
     * landmark. `Breadcrumbs` supplies its own `<nav>` inside it.
     */
    <header className={[styles.bar, className].filter(Boolean).join(' ')}>
      {type === 'title' ? (
        /*
         * Figma `Type=Title` > `Wrapper` — same 8/24 padding as the breadcrumb
         * wrapper, but MIN-aligned with a `space/0` gap rather than
         * space-between, because it has a single child.
         */
        <div
          className={[styles.wrapper, actionsWrapper ? undefined : styles.wrapperTitle]
            .filter(Boolean)
            .join(' ')}
        >
          <Heading className={styles.title}>
            <span className={styles.titleLabel}>{title}</span>
          </Heading>

          {actionsWrapper}
        </div>
      ) : (
        <div className={styles.wrapper}>
          <Breadcrumbs
            items={breadcrumbs ?? []}
            label={breadcrumbLabel}
            className={styles.breadcrumbs}
          />

          {actionsWrapper}
        </div>
      )}
    </header>
  );
}
